import ArticleLayout from '../../components/ArticleLayout';
import KoldIBundenFigure from '../../components/figures/KoldIBundenFigure';

export default function RadiatorKoldIBunden() {
  return (
    <ArticleLayout
      title="Radiator kold i bunden: Hvornår det er normalt, og hvornår det er en fejl"
      metaTitle="Radiator kold i bunden og varm i toppen? Sådan skal den være"
      metaDescription="Er radiatoren kold i bunden og varm i toppen? Læs hvorfor det er et sundhedstegn på fjernvarme, og hvornår det kræver handling."
      canonicalPath="/radiator-kold-i-bunden"
      introLead="En radiator, der er varm i toppen og kølig eller decideret kold i bunden, er i langt de fleste tilfælde ikke defekt. Det er tværtimod det korrekte tegn på, at anlægget afkøler varmevandet optimalt."
      introAnswer="Når varmt vand løber ind i toppen af radiatoren, afgiver det sin energi til rummet på vej nedad. Hvis bunden er kold og rummet har den ønskede temperatur (typisk 20–21 °C), fungerer radiatoren perfekt. Det er først et problem, hvis toppen er kold, eller hvis rummet ikke kan varmes op."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Er bunden for kold eller helt normal?' },
        { id: 'hvorfor-kold', title: '2. Fysikken bag: Hvorfor bunden skal være kølig' },
        { id: 'fejlene', title: '3. De tre situationer hvor kold bund er en fejl' },
        { id: 'omvendt', title: '4. Omvendt: Kold i toppen og varm i bunden' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Vurdér din radiators temperaturfordeling"
      checklist={[
        {
          title: 'Mål rummets faktiske temperatur med et termometer',
          description: 'Viser termometeret den ønskede temperatur på ca. 20–21 °C, passer varmeydelsen, uanset om bunden føles kold.',
        },
        {
          title: 'Mærk overgangen fra top til bund med flad hånd',
          description: 'Der skal være en jævn temperaturfaldende gradient, hvor toppen er varmest og bunden tempereret eller kølig.',
        },
        {
          title: 'Tjek om fremløbsrøret er tilsluttet i toppen',
          description: 'På ældre anlæg kan forkert tilslutning betyde, at vandet sendes ind i bunden og forstyrrer cirkulationen.',
        },
        {
          title: 'Skru termostaten op på trin 5 i 15 minutter for at teste flow',
          description: 'Hvis hele radiatoren hurtigt bliver gennemvarm ved fuld åbning, er ventilen intakt og uden forstoppelse.',
        },
        {
          title: 'Stil termostaten tilbage på trin 3 efter endt kontrol',
          description: 'Lad termostaten regulere naturligt tilbage til den balancerede tilstand med kølig bund.',
        },
      ]}
      diagram={<KoldIBundenFigure />}
      sections={[
        {
          id: 'hvorfor-kold',
          title: 'Fysikken bag: Hvorfor bunden skal være kølig',
          content: (
            <>
              <p>
                En radiator virker ved konvektion og varmestråling. Det varme vand fra fjernvarmeværket eller centralvarmekedlen ledes ind i toppen via termostatventilen med en temperatur på typisk <span className="font-mono">60–70 °C</span>.
              </p>
              <p>
                Efterhånden som vandet bevæger sig nedad gennem radiatorens kanaler, afgiver det varme til stålet og den omgivende luft. Når vandet når bunden, er det afkølet til måske <span className="font-mono">25–35 °C</span>. Dette kaldes god afkøling. Hvis radiatoren var brændende varm i bunden, ville vandet forlade huset uden at have afgivet sin varme, hvilket udløser strafafgift fra fjernvarmeselskabet.
              </p>
            </>
          ),
        },
        {
          id: 'fejlene',
          title: 'De tre situationer hvor kold bund er en reel fejl',
          content: (
            <>
              <p>
                Kold bund er kun et problem i tre specifikke scenarier:
              </p>
              <p>
                <strong>1. Rummet er koldt:</strong> Hvis termostaten står på trin 4 eller 5, men rummet ikke kan nå mere end 16–17 °C, og kun de øverste 5 cm af radiatoren er lunkne, er vandgennemstrømningen utilstrækkelig. Det kan skyldes en delvist fastgroet ventil eller for lav forindstilling.
              </p>
              <p>
                <strong>2. Slam i radiatorens bund:</strong> I ældre jernradiatorer kan der med årene samle sig magnetit og rustslam i bunden, der blokerer de nederste passager.
              </p>
              <p>
                <strong>3. Utilstrækkeligt anlægstryk:</strong> Hvis vandtrykket er for lavt til at drive flowet igennem, opnås ikke fuld cirkulation.
              </p>
            </>
          ),
        },
        {
          id: 'omvendt',
          title: 'Omvendt: Kold i toppen og varm i bunden',
          content: (
            <>
              <p>
                Hvis radiatoren derimod er iskold i toppen og varm eller lunken i bunden, er årsagen næsten med statsgaranti luft i radiatoren. Luft vil altid stige op og samle sig under radiatorens øverste flange.
              </p>
              <p>
                Dette løses let ved en almindelig udluftning med radiatornøgle, indtil luften er evakueret og der kommer vand ud af luftskruen.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal VVS-installatøren ind over?',
        content: (
          <p>
            Hvis radiatoren er skoldhed i toppen men iskold i bunden, mens rummet forbliver uopvarmet trods udluftning og fuldt åben termostat, kan der være tale om en tilstoppet ventil, et forkert monteret to-strenget system eller manglende strengindregulering. Her skal en fagmand indregulere anlæggets differenstryk og ventiler.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Er det farligt eller dyrt, at radiatoren er kold i bunden?',
          a: 'Tværtimod. På fjernvarme er det et mål at opnå størst mulig afkøling. Jo koldere vandet er, når det returneres til fjernvarmeværket, desto mere effektivt har du udnyttet energien.',
        },
        {
          q: 'Hvor varm skal en radiator være i toppen?',
          a: 'Toppens temperatur afhænger af udendørstemperaturen og varmeanlæggets fremløbstemperatur. På en kold vinterdag kan toppen være 55–65 °C, mens den på milde dage blot er lunken.',
        },
        {
          q: 'Hvad betyder det, hvis radiatoren er brandvarm både i top og bund?',
          a: 'Det betyder, at vandet suser for hurtigt igennem uden at nå at afkøle. Det giver dårlig varmeudnyttelse og kan medføre efterregning i form af afkølingstillæg fra fjernvarmeselskabet.',
        },
        {
          q: 'Kan møbler foran radiatoren gøre bunden koldere?',
          a: 'Tunge møbler og lange gardiner spærrer for den naturlige luftcirkulation. Hvis luften ikke kan cirkulere omkring radiatoren, lukker termostaten for tidligt, fordi der ophobes varme bag møblet.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'tjeklisten fra det simple til fagmanden' },
        { to: '/fjernvarme-radiator-virker-ikke', text: 'Fjernvarme og radiatorer', desc: 'afkøling, returtemperatur og forindstilling' },
        { to: '/udluftning-af-radiator', text: 'Udluftning af radiator', desc: 'sådan fjerner du luftlommen i toppen' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen', desc: 'undgå møbler foran radiatoren og hold mindst 20 cm frirum' },
      ]}
      sources={[
        { url: 'https://kredslob.dk/privat/fjernvarme/returtemperatur', label: 'Få styr på returtemperaturen og spar på varmeregningen Kredsløb' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/saadan-faar-du-mest-ud-af-fjernvarmen', label: 'Sådan får du mest ud af fjernvarme | SparEnergi' },
      ]}
    />
  );
}
